import React, { useEffect, useRef, useState } from 'react';
import { Button, InputAdornment, Icon, Typography, FormControlLabel, Checkbox } from '@material-ui/core';
import { TextFieldFormsy } from '@fuse';
import Formsy from 'formsy-react';
import * as authActions from 'app/auth/store/actions';
import { useDispatch, useSelector } from 'react-redux';
import Error from 'app/fuse-layouts/shared-components/Error';
import * as ActionsDialog from 'app/store/actions';
import DividerWithText from '../util/DividerWithText';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

const useStyles = makeStyles(theme => ({
	toggleButton: {
		cursor: 'pointer',
		color: theme.palette.primary.main,
		fontWeight: 500,
		'&:hover': {
			textDecoration: 'underline'
		}
	},
	inputField: {
		'& .MuiInputBase-input': {
			fontSize: '1.1rem',
			padding: '16px 14px'
		},
		'& .MuiInputLabel-outlined': {
			fontSize: '1.1rem'
		}
	}
}));

const CampaignLoginForm = ({ slug, onToggleSignup, campaign }) => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const formRef = useRef(null);

	const error = useSelector(({ auth }) => auth.auth.error);
	const licenseType = useSelector(({ licenseKey }) => licenseKey.key.licenseType);

	const [isFormValid, setIsFormValid] = useState(false);
	const [changed, setChanged] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [rememberMe, setRememberMe] = useState(false);

	useEffect(() => {
		if (error) {
			if (formRef.current && error.type === 'form') {
				let message = error.message;
				if (error === 'Unauthorized') message = error;
				const location = { username: message, password: message };
				formRef.current.updateInputsWithError({
					...location
				});
				disableButton();
			} else if (error.type === 'modal' && error.message) {
				dispatch(
					ActionsDialog.openDialog({
						children: <Error message={`${error.message}...`} clearError={() => dispatch(authActions.clearError())} />,
						maxWidth: 'xs',
						fullWidth: true,
						onClose: (event, reason) => {
							if (reason !== 'backdropClick') {
								dispatch(ActionsDialog.closeDialog());
							}
						}
					})
				);
			}
		}
	}, [dispatch, error, licenseType]);

	useEffect(() => {
		if (error && !changed) {
			dispatch(authActions.clearError());
		}
	}, [dispatch, error, changed]);

	const disableButton = () => setIsFormValid(false);

	const enableButton = () => setIsFormValid(true);

	const handleSubmit = model => {
		const data = {
			...model,
			username: model.username.toLowerCase(),
			slug // Include campaign slug
		};

		disableButton();
		setChanged(false);
		dispatch(authActions.submitLocalLogin(data));
	};

	return (
		<div className="w-full pb-16">
			<Formsy
				onValidSubmit={handleSubmit}
				onValid={enableButton}
				onInvalid={disableButton}
				onChange={() => setChanged(true)}
				ref={formRef}
				className="flex flex-col justify-center w-full"
			>
				<TextFieldFormsy
					className={clsx("mb-16", classes.inputField)}
					type="text"
					name="username"
					id="campaign_login_form_username"
					label="Username"
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">
								<Icon className="text-20" color="action">
									email
								</Icon>
							</InputAdornment>
						)
					}}
					variant="outlined"
					required
				/>

				<TextFieldFormsy
					className={clsx("mb-8", classes.inputField)}
					type={showPassword ? "text" : "password"}
					name="password"
					id="campaign_login_form_password"
					label="Password"
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">
								<Icon
									className="text-20 cursor-pointer"
									color="action"
									onClick={() => setShowPassword(!showPassword)}
								>
									{showPassword ? 'visibility_off' : 'visibility'}
								</Icon>
							</InputAdornment>
						)
					}}
					variant="outlined"
					required
				/>

				{/* Remember Me Section */}
				<div className="flex justify-end mb-16">
					<FormControlLabel
						control={
							<Checkbox
								checked={rememberMe}
								onChange={(e) => setRememberMe(e.target.checked)}
								color="primary"
								size="small"
							/>
						}
						label={<Typography variant="body2">Remember my credentials</Typography>}
					/>
				</div>

				<Button
					type="submit"
					variant="contained"
					id="campaign_login_form_submit"
					color="primary"
					className="w-full mx-auto mt-16"
					aria-label="Sign in"
					disabled={!isFormValid}
				>
					Sign in
				</Button>
			</Formsy>

			{/* Toggle to Signup */}
			<div className="flex flex-col items-center justify-center pt-16">
				<DividerWithText text="Or" style={{ margin: '16px 0' }} />
				<Button
					variant="outlined"
					color="primary"
					onClick={onToggleSignup}
					className="w-full mx-auto mt-8"
				>
					Create account
				</Button>
			</div>
		</div>
	);
};

export default CampaignLoginForm;
